import { useQueryClient } from '@tanstack/react-query';
import { CircleHelp, Pencil, Sparkles } from 'lucide-react';
import { useContext, useEffect, useId, type ReactNode } from 'react';
import { ICP_JEV_ITEMS_MAX, ICP_MUST_RATING, type JobDetail } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Popover, usePopover } from '../../../../components/Popover';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ToneChip } from '../../../../components/Tone';
import { useCanEdit } from '../../../../stores/session';
import { budgetNoteOf } from '../../../icp/budget-note';
import { IcpInlineForm } from '../../../icp/components/IcpInlineForm';
import { IcpInterviewPanel } from '../../../icp/components/IcpInterviewPanel';
import type { PanelParts } from '../../../icp/components/panel-parts';
import icpStyles from '../../../icp/components/icp.module.css';
import { useIcpSetup } from '../../../icp/use-icp-setup';
import { useJevBudget } from '../../../ranking/api/jev-budget-api';
import { jevBudgetButtonOf } from '../../../ranking/api/use-jev-budget-gate';
import { jobKey, useStageBar } from '../../api/jobs-api';
import { useReportUnsaved } from './builder-saves';
import { ProfileLinesContext } from './profile-lines-store';
import { JobIdealProfile } from './JobIdealProfile';
import { missingAction, sectionMissing } from './SectionMissing';
import styles from './builder.module.css';
import profileStyles from './ProfileSection.module.css';

// JOB-13 (ADR 0062 §4, mockup M1): the ideal candidate, built where Screening criteria used to be. Empty, the interview, or the
// form — all in the middle column. Before any CVs exist the assistant reads only the job's own text. Saving is a button, not
// "as you type", because a save can start Jev reading CVs and that costs money (the one exception to QoL §7's autosave).
// ADR 0076 (workflow review C2): fewer words on screen; how the ranking reads the lines sits behind one "?" beside the title.

const EMPTY_TITLE = 'Who would be perfect for this job?';
const EMPTY_LEAD = 'List what their CV should show, then rate each line. The ranking puts the closest matches first.';
const EMPTY_HOW = 'About 3 minutes · 5–8 questions · about 5–10¢';
const ASSISTANT_NOTE = 'It reads only the job’s own text, so it works before any CVs arrive.';
const FORM_LEAD = `Rate each line 1 to 5. ${ICP_MUST_RATING} means must have.`;
const FORM_LEAD_DRAFT = `Drafted from the job and your answers. Rate each line 1 to 5; ${ICP_MUST_RATING} means must have.`;
const READ_ONLY_NOTE = 'Only recruiters and HR set the ideal candidate.';
const NOT_CONNECTED_NOTE = 'Jev isn’t connected, so CVs aren’t read: this job is ranked on hard facts only.';
const PROFILE_SOURCE = 'profile';

interface ProfileSectionProps {
  readonly job: JobDetail;
  readonly isReadOnly: boolean;
  /** Somebody else saved this job first: the page reloads the section (the profile's boxes save as you type). */
  readonly onConflict: (message: string) => void;
  /**
   * Shown above the section in every state — the place for what the requester wrote about the ideal candidate
   * (workflow review B1), so it can be read, and turned into lines, before the lines below.
   */
  readonly lead?: ReactNode;
}

export function ProfileSection({ job, isReadOnly, onConflict, lead }: ProfileSectionProps): ReactNode {
  return (
    <div className={styles.section}>
      {lead}
      <ProfileBody job={job} isReadOnly={isReadOnly} onConflict={onConflict} />
    </div>
  );
}

function ProfileBody({ job, isReadOnly, onConflict }: Omit<ProfileSectionProps, 'lead'>): ReactNode {
  const canEdit = useCanEdit('application');
  const queryClient = useQueryClient();
  const stageBar = useStageBar(job.id);
  const cvCount = stageBar.data?.stages.find((stage) => stage.key === 'ranking')?.count ?? 0;
  const setup = useIcpSetup({
    jobId: job.id,
    cvCount,
    // The tick in the section list and the open rule come from the job, so it is read again.
    onSaved: () => queryClient.invalidateQueries({ queryKey: jobKey(job.id) }),
  });
  // ADR 0070 stage 2: the job's Jev budget on the buttons that spend. The interview is priced by use: off only when nothing is left.
  const budget = useJevBudget(job.id).data;
  const interviewBudget = jevBudgetButtonOf(budget, 0);
  const missing = sectionMissing(job, 'profile');
  const lines = useContext(ProfileLinesContext);
  useEffect(() => {
    lines?.set(setup.items);
    return () => lines?.set(null);
  }, [lines, setup.items]);

  useReportUnsaved(PROFILE_SOURCE, setup.isUnsaved ? setup.saveUnsaved : null);
  // ADR 0092: the Ideal candidate profile's eight steps from the hire request, which HR edits here, with the one-click draft of
  // Jev's lines, sit at the top of the section.
  const profileSteps = (
    <JobIdealProfile job={job} canEdit={!isReadOnly && canEdit} onConflict={onConflict} isDrafting={setup.isDraftingFromRequest} onDraft={setup.fromRequest} budget={interviewBudget} />
  );

  return (
    <>
      {profileSteps}
      {profileView()}
    </>
  );

  function profileView(): ReactNode {
    if (isReadOnly || !canEdit) {
      return <Card title="Ideal candidate" meta={READ_ONLY_NOTE} action={missingAction(missing)} />;
    }
    if (setup.hasError) {
      return <Card title="Ideal candidate" meta="The ideal candidate didn’t load." action={<Button size="sm" onClick={setup.retry}>Try again</Button>} />;
    }
    // Skeleton only while nothing has been on screen: a saved profile is not known until the first answer lands.
    if (setup.isLoading) {
      return (
        <Card title="Ideal candidate">
          <SkeletonLines count={6} />
        </Card>
      );
    }

    const inline = (action: ReactNode, title: string): ((parts: PanelParts) => ReactNode) => (parts: PanelParts): ReactNode => (
      <Card title={title} meta={parts.sub} action={action}>
        <div className={icpStyles.setupBody}>{parts.children}</div>
        <div className={icpStyles.setupFootStack}>{parts.footer}</div>
      </Card>
    );

    if (setup.panel === 'interview') {
      return (
        <IcpInterviewPanel
          detail={job}
          interview={setup.interview}
          step={setup.interviewStep}
          costLabel={setup.interviewCost}
          budget={interviewBudget}
          onStart={setup.startInterview}
          onAnswer={setup.answer}
          onNext={setup.nextQuestion}
          onDraft={setup.draftNow}
          onClose={setup.stop}
          renderPanel={inline(
            <Button size="sm" variant="ghost" onClick={setup.stop}>
              Stop
            </Button>,
            'Ideal candidate',
          )}
        />
      );
    }

    if (setup.panel === 'form') {
      const isReady = missing.length === 0 && !setup.isUnsaved;
      return (
        <Card
          title="Ideal candidate"
          meta={setup.draft?.interviewId ? FORM_LEAD_DRAFT : FORM_LEAD}
          action={
            <span className={styles.profileState}>
              {missingAction(missing)}
              {isReady && <ToneChip tone="done">Ready to open</ToneChip>}
              {setup.isUnsaved && <ToneChip tone="attention">Unsaved changes</ToneChip>}
              <HowItRanks />
            </span>
          }
        >
          <IcpInlineForm
            key={setup.formKey}
            defaultItems={setup.items ?? []}
            notes={setup.draft?.notes ?? []}
            isRemote={job.workMode === 'remote'}
            budget={budgetNoteOf(job)}
            onChange={setup.editDraft}
            footer={(form) => {
              const cost = setup.saveCost(form.items);
              // Saving ranks the CVs already in, so it is held to the budget; with no CVs to read it spends nothing.
              const save = cost === null ? null : jevBudgetButtonOf(budget, setup.saveMicroUsd(form.items), { isPriced: true });
              return (
                <>
                  <span className={icpStyles.setupFootText}>
                    {form.jevCount} of {ICP_JEV_ITEMS_MAX} lines Jev reads · facts are free ·{' '}
                    <b>{!setup.isJevConnected ? 'Jev isn’t connected' : cost === null ? 'no CVs yet, so nothing is read now' : 'CVs already in will be read'}</b>
                    {!form.canSave && !form.isBlank && <span className={icpStyles.error} role="status">{form.hasRemoteLocation ? ' This job is remote, so where they live isn’t checked.' : ' Fix the lines marked above to save.'}</span>}
                  </span>
                  <span className={icpStyles.setupFootActions}>
                    <Button variant="outline" disabled={form.isBlank} isBlocked={Boolean(interviewBudget.blockedNote)} onClick={() => setup.askMore(form.items)}>
                      Ask me more questions
                    </Button>
                    <Button variant="solid" disabled={!form.canSave} isBlocked={Boolean(save?.blockedNote)} onClick={() => void setup.save(form.items)}>
                      {cost === null ? 'Save the ideal candidate' : `Save and rank · ${cost}${save?.leftSuffix ?? ''}`}
                    </Button>
                  </span>
                  {(save?.blockedNote ?? interviewBudget.blockedNote) && (
                    <span className={icpStyles.error} role="status">
                      {save?.blockedNote ?? interviewBudget.blockedNote}
                    </span>
                  )}
                  {!setup.isJevConnected && <span className={icpStyles.setupFootText}>{NOT_CONNECTED_NOTE}</span>}
                </>
              );
            }}
          />
        </Card>
      );
    }

    return (
      <Card title="Ideal candidate" action={missingAction(missing)}>
        <div className={profileStyles.empty}>
          <div className={profileStyles.emptyText}>
            <h3 className={profileStyles.emptyTitle}>{EMPTY_TITLE}</h3>
            <p className={profileStyles.emptyLead}>{EMPTY_LEAD}</p>
          </div>
          <div className={profileStyles.choices}>
            <ChoiceTile
              icon={<Sparkles size={20} aria-hidden />}
              title={`Build with the assistant${interviewBudget.leftSuffix}`}
              detail={EMPTY_HOW}
              note={ASSISTANT_NOTE}
              isPrimary
              isBlocked={Boolean(interviewBudget.blockedNote)}
              onClick={setup.build}
            />
            <ChoiceTile icon={<Pencil size={20} aria-hidden />} title="Write it yourself" detail="Start from an empty list" onClick={setup.write} />
          </div>
          {interviewBudget.blockedNote && (
            <p className={styles.help} role="status">
              {interviewBudget.blockedNote}
            </p>
          )}
        </div>
      </Card>
    );
  }
}

/** One of the two ways in: a large button whose name is its title, its words read out as its description. */
function ChoiceTile({
  icon,
  title,
  detail,
  note,
  isPrimary = false,
  isBlocked = false,
  onClick,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly detail: string;
  readonly note?: string;
  readonly isPrimary?: boolean;
  readonly isBlocked?: boolean;
  readonly onClick: () => void;
}): ReactNode {
  const titleId = useId();
  const detailId = useId();
  return (
    <button
      type="button"
      className={profileStyles.choice}
      data-primary={isPrimary || undefined}
      aria-labelledby={titleId}
      aria-describedby={detailId}
      aria-disabled={isBlocked || undefined}
      onClick={() => {
        if (!isBlocked) onClick();
      }}
    >
      <span className={profileStyles.choiceIcon}>{icon}</span>
      <span className={profileStyles.choiceText}>
        <span id={titleId} className={profileStyles.choiceTitle}>
          {title}
        </span>
        <span id={detailId} className={profileStyles.choiceDetail}>
          <span>{detail}</span>
          {note && <span className={profileStyles.choiceNote}>{note}</span>}
        </span>
      </span>
    </button>
  );
}

/** C2 (ADR 0076): how the ranking reads the lines, for whoever wants it, behind one "?" beside the title. */
function HowItRanks(): ReactNode {
  const { triggerRef, anchor, isOpen, toggle, close } = usePopover();
  return (
    <>
      <button ref={triggerRef} type="button" className={profileStyles.helpButton} aria-label="How the ranking uses these lines" aria-expanded={isOpen} onClick={toggle}>
        <CircleHelp size={18} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} align="right" width={340} label="How the ranking uses these lines">
        <div className={profileStyles.helpBody}>
          <strong>How the ranking uses these lines</strong>
          <ul>
            <li>
              <b>Skills and work habits:</b> Jev reads each CV for them, up to {ICP_JEV_ITEMS_MAX} lines.
            </li>
            <li>
              <b>English:</b> Jev’s check of their written English.
            </li>
            <li>
              <b>Hard facts:</b> worked out from the application, for free.
            </li>
            <li>
              The rating is how much a line counts. Anyone missing a line rated {ICP_MUST_RATING} stays below everyone who has them all.
            </li>
          </ul>
        </div>
      </Popover>
    </>
  );
}
